<template>
  <b-modal
    :id="this.$attrs.id"
    v-model="isVisible"
    title="Resend declarations to customs"
    size="lg"
    @show="loadData"
  >
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <div v-else>
      <div v-if="!declarationsCount">
        <h4 class="pb-1">
          There are no declarations to send
        </h4>
      </div>
      <div v-else>
        <validation-observer ref="resendDeclarations">
          <h4 class="pb-1">
            You are about send {{ declarationsCount }} declarations to customs. Only declarations without MRN will be sent.
          </h4>
          <b-form-group
            v-if="customs === 'RO'"
            label="Previous document category"
            label-for="previous_document_category"
          >
            <validation-provider
              #default="{ errors }"
              name="Choose document category"
            >
              <b-form-select
                id="previous_document_category"
                v-model="previous_document_category"
                :options="previous_document_categories"
                placeholder="Choose document type"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="customs !== 'LT'"
            label="Previous document type"
            label-for="previous_document_type-input"
          >
            <validation-provider
              ref="previousDocumentTypeProvider"
              #default="{ errors }"
              name="Choose document type"
              :rules="previousDocumentRules"
            >
              <classifier-select
                id="previous_document_type"
                v-model="previous_document_type"
                :classifier-key="classifierKeys.previousDocumentTypes"
                :country="previousDocumentCountry"
                :empty-code-selectable="previousDocumentCountry === 'NL'"
                :state="errors.length > 0 ? false:null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="customs !== 'LT' && !(customs === 'NL' && previous_document_type === '')"
            label="Previous document reference"
            label-for="previous_document_reference"
          >
            <validation-provider
              ref="previousDocumentReferenceProvider"
              #default="{ errors }"
              name="Previous document reference"
              :rules="previousDocumentRules"
            >
              <b-form-input
                id="previous_document_reference"
                v-model="previous_document_reference"
                placeholder="Previous document reference"
                autocomplete="off"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </div>
    </div>

    <template #modal-footer="{ close }">
      <b-col v-if="declarationsCount">
        <b-button
          variant="secondary"
          class="mr-2"
          @click="close"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        v-if="declarationsCount"
        class="text-right"
      >
        <b-button
          variant="primary"
          :disabled="isConfirmDisabled || submitting"
          @click="validateSend()"
        >
          <span v-if="!submitting">Confirm</span>
          <b-spinner
            v-else
            small
          />
        </b-button>
      </b-col>
      <b-col
        v-if="!declarationsCount && !loading"
        class="text-right"
      >
        <b-button
          variant="secondary"
          class="mr-2"
          @click="close"
        >
          Close
        </b-button>
      </b-col>
    </template>
  </b-modal>
</template>
<script>
import { BButton, BCol, BFormGroup, BFormInput, BFormSelect, BModal, BSpinner } from 'bootstrap-vue'
import handleError from '@/views/components/errorHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { EventBus } from '@core/utils/utils'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import {
  CUSTOMS_CLASSIFIER_KEYS,
  PREVIOUS_DOCUMENT_CATEGORIES,
  classifierCountryParam,
  formatClassifierOption,
} from '@/libs/customsClassifiers'

export default {
  components: {
    ClassifierSelect,
    ValidationProvider,
    ValidationObserver,
    BCol,
    BButton,
    BFormInput,
    BFormSelect,
    BFormGroup,
    BModal,
    BSpinner,
  },
  props: {
    customs: {
      type: String,
      default: null,
    },
    shipment: {
      type: Object,
      required: true,
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
    status: {
      type: String,
      default: 'needs_attention',
    },
  },
  data() {
    return {
      isVisible: false,
      submitting: false,
      loading: false,
      previous_document_type: null,
      previous_document_category: '',
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
      previous_document_reference: '',
      declarationsCount: 0,
    }
  },
  computed: {
    // Resolved by the customs country this dialog is given, not by the
    // shipment's entry country. The shared classifier client owns the request,
    // so opening this dialog on a screen that already holds the list for the
    // same country makes no request of its own.
    previousDocumentCountry() {
      return classifierCountryParam(this.customs)
    },
    // The shared X/Y/Z constant, rendered by the shared formatter so the
    // category reads as code plus description like every classifier list.
    previous_document_categories() {
      return PREVIOUS_DOCUMENT_CATEGORIES.map(option => ({
        value: option.code,
        text: formatClassifierOption(option),
      }))
    },
    isConfirmDisabled() {
      return this.customs === 'NL' && this.previous_document_type === null
    },
    // FI H1: the backend accepts a missing previous document on resend (same
    // validation rules as initial submission), so don't hard-require it
    // client-side. Tulli still rejects standard declarations without one
    // (NC0022) — that surfaces as a server-side error.
    previousDocumentRules() {
      if (this.customs === 'FI' && this.declarationType === 'h1') {
        return ''
      }
      return (this.customs !== 'RO' && !(this.customs === 'NL' && this.previous_document_type === '')) ? 'required' : ''
    },
  },
  watch: {
    previous_document_type(newVal) {
      if (this.customs === 'NL' && newVal === '') {
        this.previous_document_reference = ''
      }

      this.$nextTick(() => {
        if (this.$refs.previousDocumentTypeProvider) {
          this.$refs.previousDocumentTypeProvider.reset()
        }
        if (this.$refs.previousDocumentReferenceProvider) {
          this.$refs.previousDocumentReferenceProvider.reset()
        }
      })
    },
  },
  methods: {
    loadData() {
      this.loading = true
      this.getResendDeclarations()
    },
    getResendDeclarations() {
      this.$http.get(`/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/${this.declarationType}/resend-declarations/${this.status}`)
        .then(response => {
          this.declarationsCount = response.data.total
          this.previous_document_type = response.data.previous_document_type
          this.previous_document_reference = response.data.previous_document_reference
          this.previous_document_category = response.data.previous_document_category
          this.loading = false
        })
    },
    validateSend() {
      this.$refs.resendDeclarations.validate()
        .then(success => {
          if (!success) {
            return
          }
          this.submit()
        })
    },
    submit() {
      this.submitting = true
      this.$http.post(`/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/${this.declarationType}/resend-declarations/${this.status}`, {
        previous_document_type: this.previous_document_type,
        previous_document_reference: this.previous_document_reference,
        previous_document_category: this.previous_document_category,
      })
        .then(() => {
          this.previous_document_type = null
          this.previous_document_category = ''
          this.previous_document_reference = ''
          this.isVisible = false

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sending declarations to customs',
              icon: 'EditIcon',
              variant: 'success',
            },
          })

          EventBus.$emit('general-declaration-update', null)
          EventBus.$emit('refresh-statistics')
          EventBus.$emit('renewDeclarationList')
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>
